import { useRef } from 'react';

export const BLEED = 16;

let _gidc = 0;

// One cherry-blossom petal: narrow base at (0,0), widening to a NOTCHED (heart-
// tipped) tip pointing "up". The little dip back to (0,-9) between the two outer
// tips (±2,-10.6) is the sakura petal's signature cleft.
const PETAL = 'M 0 0 C -4 -2.6 -4.6 -7.6 -2 -10.6 L 0 -9 L 2 -10.6 C 4.6 -7.6 4 -2.6 0 0 Z';

// A single 5-petal cherry blossom with a pollen-tipped stamen cluster. Discrete
// transforms are composed into a single transform string for web SVG.
function Blossom({ g, cx, cy, s = 1, rot = 0 }) {
  return (
    <g transform={`translate(${cx} ${cy})`}>
      <g transform={`rotate(${rot}) scale(${s})`}>
        {[0, 72, 144, 216, 288].map(d => (
          <path key={d} d={PETAL} fill={`url(#${g}petal)`} stroke="#e07b9c" strokeWidth={0.4} transform={`rotate(${d})`} />
        ))}
        {/* stamens: five short filaments tipped with pollen, radiating from centre */}
        {[20, 92, 164, 236, 308].map(d => (
          <circle key={d} cx={0} cy={-3.4} r={0.7} fill="#f0aa3e" transform={`rotate(${d})`} />
        ))}
        <circle r={1.4} fill="#f6c75a" />
      </g>
    </g>
  );
}

// A single drifting petal (a fallen blossom petal), for the bleed only.
function FallingPetal({ g, cx, cy, s = 1, rot = 0 }) {
  return (
    <g transform={`translate(${cx} ${cy})`}>
      <g transform={`rotate(${rot}) scale(${s})`}>
        <path d={PETAL} fill={`url(#${g}petal)`} stroke="#e07b9c" strokeWidth={0.4} opacity={0.85} />
      </g>
    </g>
  );
}

// A garland of cherry blossoms ringing the whole border (not just corner sprigs).
// Each flower's centre sits a couple px OUTSIDE the card edge so it blooms into
// the bleed and only its inner ~8px overlaps the very edge — clear of the 14px
// content padding. Sizes/rotations vary deterministically by index for a natural,
// hand-strewn look. A soft rose double-border sits behind for cohesion.
export default function SakuraFrame({ w, h, radius = 12 }) {
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = `sk${++_gidc}_`;
  const g = idRef.current;

  const W = w + BLEED * 2;
  const H = h + BLEED * 2;
  const x = BLEED;
  const y = BLEED;
  const r = radius + 2;
  const rIn = Math.max(2, r - 3);

  // Build the ring of blossom positions. `o` nudges centres just outside the card
  // box so flowers bloom outward into the bleed.
  const o = 2;
  const left = x - o, right = x + w + o, top = y - o, bottom = y + h + o;
  const blossoms = [];
  const add = (cx, cy) => {
    const i = blossoms.length;
    const s = 0.6 + ((i * 37) % 32) / 100;   // 0.60 .. 0.91
    const rot = (i * 53) % 360;
    blossoms.push({ cx, cy, s, rot, key: `b${i}` });
  };
  const STEP = 50;
  // top + bottom edges (skip the very corners — corner clusters fill those)
  for (let px = x + 16; px <= x + w - 16; px += STEP) { add(px, top); add(px, bottom); }
  // left + right edges
  for (let py = y + 16; py <= y + h - 16; py += STEP) { add(left, py); add(right, py); }
  // corner clusters — the focal points, each a tight trio
  const corners = [[left, top, 1, 1], [right, top, -1, 1], [left, bottom, 1, -1], [right, bottom, -1, -1]];
  for (const [ccx, ccy, sx, sy] of corners) {
    add(ccx, ccy);
    add(ccx + 16 * sx, ccy + 3 * sy);   // satellite along the horizontal edge
    add(ccx + 3 * sx, ccy + 16 * sy);   // satellite along the vertical edge
  }

  // A few drifting petals, kept well inside the bleed so they never touch content.
  const petals = [
    { cx: right + 4, cy: y + h * 0.32, s: 0.9, rot: 25 },
    { cx: left - 4, cy: y + h * 0.7, s: 0.8, rot: -40 },
    { cx: x + w * 0.4, cy: top - 6, s: 0.75, rot: 120 },
  ];

  return (
    <div style={{ position: 'absolute', top: -BLEED, left: -BLEED, width: W, height: H, pointerEvents: 'none' }}>
      <svg width={W} height={H} xmlns="http://www.w3.org/2000/svg">
        <defs>
          {/* petal shading — deeper rose at the base, blushing to near-white tips */}
          <radialGradient id={`${g}petal`} cx="0.5" cy="0.62" r="0.7">
            <stop offset="0" stopColor="#ef93b2" />
            <stop offset="0.55" stopColor="#f7c0d2" />
            <stop offset="1" stopColor="#ffe3ee" />
          </radialGradient>
        </defs>

        {/* soft rose double-border behind the garland */}
        <rect x={x} y={y} width={w} height={h} rx={r} ry={r} fill="none" stroke="#f3cdd8" strokeWidth={1.3} opacity={0.9} />
        <rect x={x + 4} y={y + 4} width={w - 8} height={h - 8} rx={rIn} ry={rIn} fill="none" stroke="#e295a8" strokeWidth={0.8} opacity={0.6} />

        {/* drifting petals (behind the flowers) */}
        {petals.map((p, i) => <FallingPetal key={`p${i}`} g={g} {...p} />)}

        {/* the blossom garland */}
        {blossoms.map(b => <Blossom key={b.key} g={g} cx={b.cx} cy={b.cy} s={b.s} rot={b.rot} />)}
      </svg>
    </div>
  );
}
